import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";

import { useTranslator } from "context/translator";

const ModalMCContext = createContext(null);

export function ModalMessageConfirmationProvider({ children }) {

    const { translate } = useTranslator();

    const [state, setState] = useState({
        isOpen: false,
        mode: "message", // "message" | "confirmation"
        title: "",
        messageHtml: "",
        txtAction: "",
        txtActionCancel: "",
        focus: null, // true/false/null
    });

    const pendingResolveRef = useRef(null);
    const btnAcceptRef = useRef(null);
    const btnCancelRef = useRef(null);

    const defaults = useMemo(
        () => ({
            titleMessage: translate("message"),
            titleConfirmation: translate("confirmation"),
            txtAccept: translate("accept"),
            txtCancel: translate("cancel"),
        }),
        [translate]
    );

    const closeMessage = () => {
        setState((s) => ({
            ...s,
            isOpen: false,
            title: "",
            messageHtml: "",
        }));
    };

    const closeConfirmation = () => {
        setState((s) => ({
            ...s,
            isOpen: false,
            title: "",
            messageHtml: "",
            txtAction: "",
            txtActionCancel: "",
            focus: null,
        }));
    };

    const handleClose = () => {
        if (state.mode === "message") {
            closeMessage();
            return;
        }
        // Si cierra confirmación por overlay/ESC => false
        if (pendingResolveRef.current) {
            pendingResolveRef.current(false);
            pendingResolveRef.current = null;
        }
        closeConfirmation();
    };

    //const showMessage = (messageHtml, title) => {
    //    // Si había una confirmación pendiente, la resolvemos como false (opcional)
    //    if (pendingResolveRef.current) {
    //        pendingResolveRef.current(false);
    //        pendingResolveRef.current = null;
    //    }

    //    setState({
    //        isOpen: true,
    //        mode: "message",
    //        title: title?.trim() ? title : defaults.titleMessage,
    //        messageHtml: messageHtml ?? "",
    //        txtAction: "",
    //        txtActionCancel: "",
    //        focus: null,
    //    });
    //};

    //const confirm = (messageHtml, title, botonAccept, focus, botonCancel) => {
    //    return new Promise((resolve) => {
    //        pendingResolveRef.current = resolve;

    //        setState({
    //            isOpen: true,
    //            mode: "confirmation",
    //            title: title?.trim() ? title : defaults.titleConfirmation,
    //            messageHtml: messageHtml ?? "",
    //            txtAction: botonAccept?.trim() ? botonAccept : defaults.txtAccept,
    //            txtActionCancel: botonCancel?.trim() ? botonCancel : defaults.txtCancel,
    //            focus: focus ?? null,
    //        });
    //    });
    //};



    const ShowMessage = useCallback((messageHtml, title) => {
        if (pendingResolveRef.current) {
            pendingResolveRef.current(false);
            pendingResolveRef.current = null;
        }

        setState({
            isOpen: true,
            mode: "message",
            title: title?.trim() ? title : defaults.titleMessage,
            messageHtml: messageHtml ?? "",
            txtAction: "",
            txtActionCancel: "",
            focus: null,
        });
    }, [defaults]);

    const ShowConfirm = useCallback((messageHtml, title, botonAccept, focus, botonCancel) => {
        return new Promise((resolve) => {
            pendingResolveRef.current = resolve;

            setState({
                isOpen: true,
                mode: "confirmation",
                title: title?.trim() ? title : defaults.titleConfirmation,
                messageHtml: messageHtml ?? "",
                txtAction: botonAccept?.trim() ? botonAccept : defaults.txtAccept,
                txtActionCancel: botonCancel?.trim() ? botonCancel : defaults.txtCancel,
                focus: focus ?? null,
            });
        });
    }, [defaults]);





    const onConfirm = () => {
        if (pendingResolveRef.current) {
            pendingResolveRef.current(true);
            pendingResolveRef.current = null;
        }
        closeConfirmation();
    };

    const onCancel = () => {
        if (pendingResolveRef.current) {
            pendingResolveRef.current(false);
            pendingResolveRef.current = null;
        }
        closeConfirmation();
    };

    // Focus al abrir confirmación
    useEffect(() => {
        if (!state.isOpen || state.mode !== "confirmation") return;

        if (state.focus === true) {

            //btnAcceptRef.current?.focus();
            setTimeout(() => {
                btnAcceptRef.current?.focus();
            }, 20);


        } else if (state.focus === false) {

            //btnCancelRef.current?.focus();
            setTimeout(() => {
                btnCancelRef.current?.focus();
            }, 20);
            

        }
    }, [state.isOpen, state.mode, state.focus]);

    // ESC cierra
    useEffect(() => {
        if (!state.isOpen) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") handleClose();
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.isOpen, state.mode]);

    //const api = useMemo(() => ({ showMessage, confirm }), []); // funciones estables

    const api = useMemo(() => ({ ShowMessage, ShowConfirm }), [ShowMessage, ShowConfirm]);
     
    return (
        <ModalMCContext.Provider value={api}>
            {children}

            {/* Modal global renderizado UNA sola vez */}
            {state.isOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
                    role="dialog"
                    aria-modal="true"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) handleClose();
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg w-full max-w-md">
                        {/* Header */}
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800 mb-4 dark:text-white">
                                {state.title}
                            </h2>
                        </div>

                        {/* Body */}
                        <div>
                            <div
                                className="text-gray-600 mb-6 dark:text-gray-300"
                                dangerouslySetInnerHTML={{ __html: state.messageHtml }}
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end space-x-3">
                            {state.mode === "message" ? (
                                <button
                                    onClick={handleClose}
                                    className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 transition"
                                >
                                    { translate("close") }
                                </button>
                            ) : (
                                <>
                                    <button
                                        ref={btnCancelRef}
                                        onClick={onCancel}
                                        className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 transition"
                                        id="idBtnCancel"
                                    >
                                        {state.txtActionCancel}
                                    </button>
                                    <button
                                        ref={btnAcceptRef}
                                        onClick={onConfirm}
                                            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
                                    >
                                        {state.txtAction}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </ModalMCContext.Provider>
    );
}

export function useModalMessageConfirmation() {
    const ctx = useContext(ModalMCContext);
    if (!ctx) {
        throw new Error("useModalMessageConfirmation debe usarse dentro de ModalMessageConfirmationProvider");
    }
    return ctx;
}






//import React, {
//    forwardRef,
//    useImperativeHandle,
//    useMemo,
//    useRef,
//    useState,
//    useEffect,
//} from "react";

///**
// * Uso:
// *   ref.current.ModalMessageShow(html, title?)
// *   const ok = await ref.current.ModalConfirmationShow(html, title?, botonAccept?, focus?, botonCancel?)
// *
// * focus:
// *   true  => focus en botón Accept
// *   false => focus en botón Cancel
// *   null/undefined => no hace focus
// */
//const ModalMessageConfirmation = forwardRef(function ModalMessageConfirmation(_, ref) {
//    const [state, setState] = useState({
//        isOpen: false,
//        mode: "message", // "message" | "confirmation"
//        title: "",
//        messageHtml: "",
//        txtAction: "",
//        txtActionCancel: "",
//        focus: null, // true/false/null
//    });

//    // Guardamos resolve de la promesa cuando sea confirmación
//    const pendingResolveRef = useRef(null);

//    // Refs botones para focus
//    const btnAcceptRef = useRef(null);
//    const btnCancelRef = useRef(null);

//    const defaults = useMemo(
//        () => ({
//            titleMessage: "Message",
//            titleConfirmation: "Confirmación",
//            txtAccept: "Accept",
//            txtCancel: "Cancel",
//        }),
//        []
//    );

//    function openMessage(messageHtml, title) {
//        // Si había una promesa pendiente, la cancelamos (opcional, pero evita estados raros)
//        if (pendingResolveRef.current) {
//            pendingResolveRef.current(false);
//            pendingResolveRef.current = null;
//        }

//        setState({
//            isOpen: true,
//            mode: "message",
//            title: title?.trim() ? title : defaults.titleMessage,
//            messageHtml: messageHtml ?? "",
//            txtAction: "",
//            txtActionCancel: "",
//            focus: null,
//        });
//    }

//    function closeMessage() {
//        setState((s) => ({
//            ...s,
//            isOpen: false,
//            title: "",
//            messageHtml: "",
//        }));
//    }

//    function openConfirmation(messageHtml, title, botonAccept, focus, botonCancel) {
//        return new Promise((resolve) => {
//            pendingResolveRef.current = resolve;

//            setState({
//                isOpen: true,
//                mode: "confirmation",
//                title: title?.trim() ? title : defaults.titleConfirmation,
//                messageHtml: messageHtml ?? "",
//                txtAction: botonAccept?.trim() ? botonAccept : defaults.txtAccept,
//                txtActionCancel: botonCancel?.trim() ? botonCancel : defaults.txtCancel,
//                focus: focus ?? null, // true/false/null
//            });
//        });
//    }

//    function closeConfirmation() {
//        setState((s) => ({
//            ...s,
//            isOpen: false,
//            title: "",
//            messageHtml: "",
//            txtAction: "",
//            txtActionCancel: "",
//            focus: null,
//        }));
//    }

//    function aceptarConfirmation() {
//        if (pendingResolveRef.current) {
//            pendingResolveRef.current(true);
//            pendingResolveRef.current = null;
//        }
//        closeConfirmation();
//    }

//    function cancelarConfirmation() {
//        if (pendingResolveRef.current) {
//            pendingResolveRef.current(false);
//            pendingResolveRef.current = null;
//        }
//        closeConfirmation();
//    }

//    // Cerrar modal (overlay click o X si luego la agregas)
//    function handleClose() {
//        if (state.mode === "message") {
//            closeMessage();
//        } else {
//            // Si es confirmación y cierran sin elegir => false
//            cancelarConfirmation();
//        }
//    }

//    // Focus al abrir confirmación
//    useEffect(() => {
//        if (!state.isOpen) return;
//        if (state.mode !== "confirmation") return;

//        if (state.focus === true) {
//            setTimeout(() => btnAcceptRef.current?.focus(), 20);
//        } else if (state.focus === false) {
//            setTimeout(() => btnCancelRef.current?.focus(), 20);
//        }
//    }, [state.isOpen, state.mode, state.focus]);

//    // ESC para cerrar
//    useEffect(() => {
//        if (!state.isOpen) return;

//        const onKeyDown = (e) => {
//            if (e.key === "Escape") handleClose();
//        };

//        window.addEventListener("keydown", onKeyDown);
//        return () => window.removeEventListener("keydown", onKeyDown);
//        // eslint-disable-next-line react-hooks/exhaustive-deps
//    }, [state.isOpen, state.mode]);

//    useImperativeHandle(ref, () => ({
//        ModalMessageShow: (messageHtml, title) => openMessage(messageHtml, title),
//        ModalConfirmationShow: (messageHtml, title, botonAccept, focus, botonCancel) =>
//            openConfirmation(messageHtml, title, botonAccept, focus, botonCancel),
//    }));

//    if (!state.isOpen) return null;

//    return (
//        <div
//            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
//            role="dialog"
//            aria-modal="true"
//            onMouseDown={(e) => {
//                // cerrar si hacen click fuera del contenido
//                if (e.target === e.currentTarget) handleClose();
//            }}
//        >
//            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg w-full max-w-md">
//                {/* Header (Title) */}
//                <div>
//                    <h2 className="text-xl font-semibold text-gray-800 mb-4 dark:text-white">
//                        {state.title}
//                    </h2>
//                </div>

//                {/* Body */}
//                <div>
//                    <div
//                        className="text-gray-600 mb-6 dark:text-gray-300"
//                        dangerouslySetInnerHTML={{ __html: state.messageHtml }}
//                    />
//                </div>

//                {/* Footer (Buttons) */}
//                <div className="flex justify-end space-x-3">
//                    {state.mode === "message" ? (
//                        <button
//                            onClick={handleClose}
//                            className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 transition"
//                        >
//                            Cerrar
//                        </button>
//                    ) : (
//                        <>
//                            <button
//                                ref={btnCancelRef}
//                                onClick={cancelarConfirmation}
//                                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 transition"
//                            >
//                                {state.txtActionCancel}
//                            </button>
//                            <button
//                                ref={btnAcceptRef}
//                                onClick={aceptarConfirmation}
//                                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
//                            >
//                                {state.txtAction}
//                            </button>
//                        </>
//                    )}
//                </div>
//            </div>
//        </div>
//    );
//});

//export default ModalMessageConfirmation;
